import { PricingPageProps } from 'features/pricing/types'
import { Card, Faq, Grid, Header, Heading, Button, TextAndMedia } from '@boltenergy-be/design-system'
import { Trans, useTranslation } from 'next-i18next/pages'
import BoltOffering from 'sections/bolt-offering/BoltOffering'
import Link from 'components/link/Link'
import { routes } from 'utils/routes'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { mapCmsFaqArticleToAccordionItem } from 'utils/cms'
import PlentySection from 'sections/plenty-section/PlentySection'
import { startSimulationOrRegistration } from 'utils/customerFlow'
import { getPortalUrl } from 'utils/tracking'
import ProductsSummarySection from '../../sections/products-summary-section/ProductsSummarySection'
import parse from 'html-react-parser'

const Pricing = ({ allProducts, content, products }: PricingPageProps) => {
  // i18n
  const { t } = useTranslation(['pricing', 'common'])
  const language = useGetLanguage()

  // Constants
  const { faqCategory, hero } = content

  return (
    <main>
      <Header title={hero.title} shape="s3" docked>
        {hero.description}
        <Button
          variant="primary"
          onClick={() => {
            void startSimulationOrRegistration()
          }}
        >
          {t(($) => $.hero.cta.simulate)}
        </Button>
      </Header>

      {/* BOLT USPS */}
      <BoltOffering title={t(($) => $.offering.title)} showMediaOnMobile={false} />

      {/* PRODUCT COMPARISON */}
      <ProductsSummarySection cmsProducts={products} apiProducts={allProducts} />

      {/* INJECTION TARIFF */}
      <TextAndMedia
        inverse
        title={t(($) => $.injection.title)}
        media={{ type: 'image', props: { publicId: 'website/injection-tariff/how', alt: '', width: 600, height: 800 } }}
      >
        <p>{parse(t(($) => $.injection.description))}</p>
        <Link href={routes(language).injectionTariff} representation="button" trailingIcon="chevronRight">
          {t(($) => $.injection.action)}
        </Link>
      </TextAndMedia>

      {/* PLENTY */}
      <PlentySection description={t(($) => $.plenty.description)} title={t(($) => $.plenty.title)} />

      {/* DIGITAL METER OPTIONS */}
      <Grid title={t(($) => $.digitalMeter.title)} description={t(($) => $.digitalMeter.description)} color="accent-warm">
        <Card>
          <Heading as="h2" variant="h3" serif>
            {t(($) => $.digitalMeter.dynamic.title)}
          </Heading>
          <p>{t(($) => $.digitalMeter.dynamic.description)}</p>
          <small className="text-subdued">{t(($) => $.digitalMeter.dynamic.sidenote)}</small>
          <Link href={routes(language).dynamicPricing}>{t(($) => $.moreInfo, { ns: 'common' })}</Link>
        </Card>

        <Card>
          <Heading as="h2" variant="h3" serif>
            {t(($) => $.digitalMeter.monthly.title)}
          </Heading>
          <p>{t(($) => $.digitalMeter.monthly.description)}</p>
          <small className="text-subdued">{t(($) => $.digitalMeter.monthly.sidenote)}</small>
          <Link external href={getPortalUrl('/my-contract')} suppressHydrationWarning>
            {t(($) => $.moreInfo, { ns: 'common' })}
          </Link>
        </Card>
      </Grid>

      {/* FAQ */}
      <Faq
        description={
          <Trans
            i18nKey={($) => $.faq.description}
            ns="common"
            components={[<Link key="0" variant="inline" href={routes(language).faq} />]}
          />
        }
        articles={faqCategory.articles.map(mapCmsFaqArticleToAccordionItem)}
        title={t(($) => $.faq.title)}
      />
    </main>
  )
}

export default Pricing
